<template>
    <div
        ref="inputContainer"
        class="input-sm h-8 leading-7 rounded-none px-2 border border-base-300 dark:border-base-300/50 flex items-center relative bg-base-100"
        :class="focusClass"
    >
        <label
            v-if="label"
            :for="inputId"
            class="label"
            :class="isDisabled ? 'text-base-300':''"
        >
            {{ label }}
            <span class="label-background"/>
        </label>
        <input
            ref="amountInput"
            v-model="displayValue"
            autocomplete="off"
            placeholder="Beløp"
            data-cy="trans-entry-amount"
            :data-col-id="LINE_COLUMN.Amount"
            class="pr-2 w-full text-right focus:outline-none bg-inherit spinnerless"
            @blur="onBlur"
            @keydown.up.prevent
            @keydown.down.prevent
            @focus="onAmountFocus"
            @click="onAmountFocus"
            @keydown.enter.exact="onAmountEnterKey"
        >

        <tooltip
            v-if="inputWarning"
            placement="top"
            error-tooltip
            needs-teleporting
        >
            <template #tooltipAnchor>
                <i class="i-ph-asterisk block text-error text-xs mt-1"/>
            </template>

            <template #tooltipContent>
                <div class="flex items-center gap-2">
                    <i class="i-ph-warning-fill text-error text-lg"/>
                    <div>{{ errorString }}</div>
                </div>
            </template>
        </tooltip>
    </div>
</template>

<script setup lang="ts">
import {LINE_COLUMN} from '@/transaction/create-transaction/EntryLineInstance'
import {computed, nextTick, ref, watch} from 'vue'
import {formatCurrency} from '@/services/formatter.service'
import Tooltip from '@/components/Tooltip.vue'
import {selectInput} from '@/input-utils/inputUtils'
import {useArithmeticEvaluator} from '@/input-utils/useArithmeticEvaluator'

const props = defineProps({
    modelValue: {
        type: Number,
        required: true
    },
    isDisabled: Boolean,
    inputId: String,
    label: String,
    inputWarning: Boolean,
    errorString: String
})

const amountInput = ref(null)
const activeValue = ref('')
const isInputActive = ref(false)
const focusClass = ref('dark:bg-base-200')

const {evaluateExpression} = useArithmeticEvaluator()

const emit = defineEmits(['update:modelValue', 'updateAccrual', 'onFocus', 'onEnter'])

const displayValue = computed({
    get: () => {
        if (isInputActive.value) return activeValue.value
        return formatCurrency(props.modelValue)
    },
    set: (value: string) => {
        activeValue.value = value
    }
})

// resetting the displayValue
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue <= 0) {
            displayValue.value = formatCurrency(0, 2)
            blur()
        }
    }
)

const blur = () => {
    //@ts-ignore
    amountInput.value.blur()
}
const focus = () => {
    //@ts-ignore
    amountInput.value.focus()
}

const onAmountFocus = (event: any) => {
    focusClass.value = 'bg-base-100 dark:bg-base-100'
    activeValue.value = formatCurrency(props.modelValue, 2)
    isInputActive.value = true
    emit('onFocus', event)
    nextTick(() => {
        selectInput(event)
    })
}

const onBlur = () => {
    focusClass.value = 'dark:bg-base-200'

    let clean = activeValue.value.replaceAll(/\s/g, '').replaceAll(',', '.')
    let evaluatedValue = evaluateInputValue(clean)
    let numericValue: number = Number(parseFloat(evaluatedValue).toFixed(2))

    if (numericValue === props.modelValue) {
        displayValue.value = formatCurrency(numericValue)
        return
    }

    if (clean.match(/^(\d+):(\d{4}):(\d+)(?::(1[0-2]|0[1-9]|[1-9]))?(?::(\d{2}|\d{4}))?$/)) {
        //if the clean value matches pattern [any digit number]:[4 digit number]:[1 or 2 digit number]:[1 or 2 digit number]:[2 or 4 digit number] then split into amount, account, monthCount, startMonth and startYear
        const [amount, account, monthCount, startMonth, startYear] = clean.split(':')

        let updatedStartYear = startYear ? parseFloat(startYear) : startMonth ? new Date().getFullYear() : null
        updatedStartYear = updatedStartYear !== null && updatedStartYear < 100 ? parseFloat('20' + updatedStartYear) : updatedStartYear

        nextTick(() => {
            emit('updateAccrual', {
                account,
                monthCount: parseFloat(monthCount),
                startMonth: parseFloat(startMonth) - 1,
                startYear: updatedStartYear
            })
        })
        clean = amount
    }

    evaluatedValue = evaluateInputValue(clean)
    numericValue = Number(parseFloat(evaluatedValue).toFixed(2))

    if (isFinite(numericValue)) {
        emit('update:modelValue', numericValue)
    } else {
        emit('update:modelValue', 0)
    }
    isInputActive.value = false
}

const evaluateInputValue = (value: string) => {
    return evaluateExpression(value)
}

defineExpose({
    focus
})

const onAmountEnterKey = () => {
    emit('onEnter')
}

</script>

<style scoped>

</style>